import type { UpstreamProtocol } from './internal-upstreams.ts';
import type { PinReconciliationRow } from './internal-pin-reconciliation.ts';
import type { GatewayMutationReceipt } from './gateway-mutation-receipts.ts';
import type { RoutingSwitchEventRecord } from './internal-tenant-routing-dr.ts';

export type RouteResolutionSource =
  /**
   * Ladder tier 0 (#1422). Deliberately NOT named `override`: `org_override`
   * already means the frozen legacy org route override three tiers below, and
   * two members one word apart on the same union is how an incident reviewer
   * reads the wrong one. Never emitted by the selector's candidate rows — a
   * candidate the override produced genuinely came from a routing config, so
   * `route_resolution_source` there stays `route_config`; this member exists
   * for the effective-route preview, which must say *which* config and why.
   */
  | 'emergency_override'
  | 'route_config'
  | 'org_sku'
  | 'org_override'
  | 'sku_default'
  | 'global_default'
  | 'legacy_fallback';
export type RoutingPolicyMode = 'full_chain' | 'network_only' | 'same_cache_domain' | 'fail_fast';
export type RoutingConfigType = 'system' | 'customer' | 'experiment';
export type RoutingConfigStatus = 'draft' | 'active' | 'archived';
export type RoutingConfigCoverageMode = 'exclusive' | 'inherit';
export type RoutingConfigAssignmentScope = 'global' | 'org' | 'user';

export interface RoutingDefaultTargetRecord {
  readonly upstream_id: string;
  readonly upstream_name: string | null;
  readonly account_id: string;
  readonly account_label: string | null;
  readonly key_bucket_id: string;
  readonly key_bucket_label: string | null;
  readonly enabled: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface RoutingConfigRecord {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly config_type: RoutingConfigType;
  readonly status: RoutingConfigStatus;
  readonly version: number;
  readonly coverage_mode: RoutingConfigCoverageMode;
  readonly created_from_config_id: string | null;
  readonly is_global_default: boolean;
  readonly assigned_org_count: number;
  /**
   * Soft deprecation marker (#1919). Null means live. A timestamp means an
   * operator marked the config as one that should not receive NEW bindings —
   * it does not remove it from service, and nothing in the candidate ladder,
   * the assignment resolver, the routing cache key or preflight reads it. Orgs
   * already assigned keep routing through it exactly as before.
   */
  readonly deprecated_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * Role-projected routing config row (#1658 part A). The gateway always emits
 * the full {@link RoutingConfigRecord}; ops-api answers that record verbatim
 * to admin sessions and rebuilds this narrower shape for every other session
 * from a fail-closed allowlist, so a field added to the gateway row is absent
 * from the sales view until it is named here on purpose.
 *
 * The optional members are the admin-only remainder. `description` is the
 * internal operator note. `assigned_org_count` answers "how many other
 * customers are on this config" — the assignable library is deliberately
 * shared across all customer managers, so that count is cross-book inventory
 * and stays out of the projection.
 */
export interface RoutingConfigSummaryRecord {
  readonly id: string;
  readonly name: string;
  readonly config_type: RoutingConfigType;
  readonly status: RoutingConfigStatus;
  readonly version: number;
  readonly coverage_mode: RoutingConfigCoverageMode;
  readonly is_global_default: boolean;
  readonly created_at: string;
  readonly updated_at: string;
  /** Admin sessions only — absent from the sales projection. */
  readonly description?: string | null;
  readonly created_from_config_id?: string | null;
  readonly assigned_org_count?: number;
  /**
   * Soft deprecation marker (#1919), admin-only like the rest of this block.
   * Optional for the same reason the others are: the gateway always emits it,
   * and a narrower projection may not. A consumer that cannot see it renders
   * the config exactly as before, which is the correct degradation — the
   * marker is an operator hint, never an authorization or routing fact.
   */
  readonly deprecated_at?: string | null;
}

export interface RoutingConfigAssignmentRecord {
  readonly id: string;
  readonly scope_type: RoutingConfigAssignmentScope;
  readonly scope_id: string;
  readonly config_id: string;
  readonly config_name: string | null;
  readonly active_from: string | null;
  readonly active_until: string | null;
  readonly priority: number;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface GetOrgRoutingAssignmentsResponse {
  readonly assignments: readonly RoutingConfigAssignmentRecord[];
}

export interface SetOrgRoutingAssignmentsRequest {
  readonly config_ids: readonly string[];
}

export interface SetOrgRoutingAssignmentsResponse {
  readonly assignments: readonly RoutingConfigAssignmentRecord[];
  readonly cache?: RoutingCacheConvergence;
  readonly mutation_receipt: GatewayMutationReceipt;
}

export interface EffectiveRouteConfigRef {
  readonly id: string;
  readonly name: string | null;
}

export interface EffectiveTenantRouteRecord {
  readonly source: RouteResolutionSource | 'none';
  readonly override_id: string | null;
  readonly target: RoutingDefaultTargetRecord | null;
  /** Set when `source` is `route_config`; identifies the active routing config that produced the hit (or black hole). */
  readonly route_config?: EffectiveRouteConfigRef | null;
  /**
   * Epic #898 P5 (#908): populated instead of `target` when the preview was
   * asked for a SKU (`sku_id` query param) — the SKU-partition ladder's
   * winning row, mirroring the live selector's source tables.
   */
  readonly sku_target?: RoutingSkuTargetRecord | null;
  /** Control-plane-only diagnosis; never exposed by tenant generation APIs. */
  readonly gap?: EffectiveTenantRouteGap | null;
}

export interface EffectiveTenantRouteGap {
  /**
   * `uncovered_by_emergency_override` is its own reason rather than a reuse of
   * `uncovered_by_assigned_config` because the remedy is the opposite one. An
   * org config that does not cover a SKU is fixed by editing that org's
   * config; a SKU the *engaged override* does not cover is failing closed for
   * every tenant at once, and the fix is to widen the DR config or disengage.
   */
  readonly reason: 'uncovered_by_assigned_config' | 'uncovered_by_emergency_override';
  readonly sku_id: string;
  readonly config: EffectiveRouteConfigRef;
}

export interface GetRoutingDefaultTargetResponse {
  readonly target: RoutingDefaultTargetRecord | null;
}

export interface SetRoutingDefaultTargetRequest {
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly enabled?: boolean;
}

export interface SetRoutingDefaultTargetResponse {
  readonly target: RoutingDefaultTargetRecord;
}

export interface ClearRoutingDefaultTargetResponse {
  readonly cleared: true;
}

/**
 * Role-filtered by ops-api on `/v1/ops/tenant-routing/configs` (#1658):
 * admin sessions receive the full library as {@link RoutingConfigRecord};
 * every other session receives only active `customer`-type non-global configs
 * projected to the required members of {@link RoutingConfigSummaryRecord},
 * and only the assignment rows whose org is inside the caller's customer book.
 */
export interface ListRoutingConfigsResponse {
  readonly configs: readonly RoutingConfigSummaryRecord[];
  readonly assignments: readonly RoutingConfigAssignmentRecord[];
}

/** Cross-process cache convergence evidence returned by routing mutations. */
export interface RoutingCacheConvergence {
  readonly revision: string | null;
  readonly convergence: 'immediate' | 'degraded';
  readonly effective_after: string;
  readonly scopes: readonly string[];
}

export interface AssignRoutingConfigRequest {
  readonly scope_type: RoutingConfigAssignmentScope;
  readonly scope_id?: string;
  readonly config_id: string;
}

export interface AssignRoutingConfigResponse {
  readonly assignment: RoutingConfigAssignmentRecord;
  readonly cache?: RoutingCacheConvergence;
  readonly mutation_receipt: GatewayMutationReceipt;
}

/**
 * Maximum length of a global-switch `reason`, after trimming.
 *
 * Lives in the contract rather than in each side because the two failure
 * directions are asymmetric. If the gateway's limit were *lower* than the
 * console's, the console over-sends and the gateway's `INVALID_PAYLOAD` is
 * caught and reported — noisy but safe. If the gateway's limit were *higher*,
 * the console's `maxLength` would silently truncate the operator's text with
 * no error at all, and an incident reason would quietly lose its tail. Two
 * numbers held together by a comment can drift either way; one exported
 * constant cannot.
 */
export const MAX_SWITCH_REASON_LENGTH = 1_000;

/**
 * Body of `PUT /internal/tenant-routing/configs/:configId/global-default`.
 *
 * There is deliberately no `config_id`: the target comes from the path. It
 * used to be here, was never read by the gateway, and forced the only client
 * to `Omit` it — two sources for one fact, one of which nothing validated.
 */
export interface SetGlobalRoutingConfigRequest {
  /**
   * Required (#1421). A global switch with no stated reason produces a
   * `routing_switch_events` row nobody can act on during the next incident,
   * which is the failure this log exists to remove.
   */
  readonly reason: string;
  /**
   * Optional in phase 3a, tightened to required in a follow-up, so deploying
   * this phase cannot break an in-flight incident response.
   *
   * When present it must be a token issued by
   * `POST /internal/tenant-routing/configs/:configId/preflight`. The gateway
   * recomputes the token's *gate* half from live state and rejects a mismatch
   * with `ROUTING_PREFLIGHT_TOKEN_STALE`.
   *
   * ── What a matching token proves ─────────────────────────────────────────
   * That the candidate config and the current global default still have the
   * same identity, binding content (`routing_config_sku_targets` +
   * `routing_config_sku_policies`), coverage mode and `updated_at` they had
   * when the preflight ran. In other words: nobody edited either config, and
   * nobody else switched the global default, between reading the report and
   * acting on it.
   *
   * ── What it does NOT prove ───────────────────────────────────────────────
   * Read this before treating a green gate as a go-ahead. The token says
   * nothing about:
   *
   *  - **supply health** — an upstream disabled, a capability dropped from the
   *    live set, a breaker opened, capacity depleted or an egress profile
   *    disabled since the preflight all leave the token matching. The
   *    report's `target_health` block is a point-in-time reading, and the
   *    token does not refresh it.
   *  - **the tiers below the global config** — `routing_default_sku_targets` /
   *    `routing_default_sku_policies`, which an `inherit` candidate falls
   *    through to, are in no config's content digest.
   *  - **org-scoped routing** — a new exclusive org assignment can make the
   *    switch not reach an org, invisibly to the gate.
   *  - **evaluation scope** — the token's scope half (window length and the
   *    `(sku_id, protocol)` pairs walked) is deliberately ignored by the gate,
   *    because it drifts on its own as traffic ages out of a sliding window.
   *    A matching token therefore does not tell you the report covered a
   *    useful window or a meaningful number of SKUs.
   *  - **that a preflight was run at all** — any caller who can reach this
   *    endpoint can also run a preflight and mint a fresh token. This is a
   *    staleness detector, not an authenticity proof.
   */
  readonly preflight_token?: string;
}

export interface SetGlobalRoutingConfigResponse {
  readonly assignment: RoutingConfigAssignmentRecord;
  readonly cache?: RoutingCacheConvergence;
  readonly mutation_receipt: GatewayMutationReceipt;
  /**
   * The `routing_switch_events` row this switch wrote, in the same transaction
   * as the assignment change (#1421). Typed through the DR module to keep the
   * event shape in one place.
   */
  readonly event: RoutingSwitchEventRecord;
  /**
   * True when the emergency override (#1422) is engaged and effective at the
   * moment this switch committed — in which case **this switch changed
   * nothing observable**. Tier 0 sits above the global assignment, so every
   * tenant stays on the override's config; the assignment moved, the cache
   * converged, and the route did not.
   *
   * Reported so the operator surface can say that instead of showing a
   * successful switch and a restored config while the fleet is elsewhere.
   */
  readonly override_in_force?: boolean;
}

export interface CreateRoutingConfigFromDefaultRequest {
  readonly name: string;
  readonly description?: string | null;
  readonly assign_org_id?: string;
}

export interface CreateRoutingConfigFromDefaultResponse {
  readonly config: RoutingConfigRecord;
  readonly assignment: RoutingConfigAssignmentRecord | null;
  readonly cache?: RoutingCacheConvergence;
  readonly mutation_receipt: GatewayMutationReceipt;
  /**
   * Copied pins that no longer satisfy the canonical egress contract (#1384,
   * plan D09). ADDITIVE — same back-compat pattern as `cache`. The rows WERE
   * copied verbatim; this reports which of them the selector will now skip,
   * so the operator learns it at clone time instead of from missing supply.
   * Absent or empty both mean "nothing stranded".
   */
  readonly incompatible_pins?: readonly PinReconciliationRow[];
}

/** `PATCH /internal/tenant-routing/configs/:configId` — rename a routing config. */
export interface RenameRoutingConfigRequest {
  readonly name: string;
}

export interface RenameRoutingConfigResponse {
  readonly config: RoutingConfigRecord;
  readonly mutation_receipt: GatewayMutationReceipt;
}

export interface SetRoutingConfigCoverageModeRequest {
  readonly coverage_mode: RoutingConfigCoverageMode;
}

export interface SetRoutingConfigCoverageModeResponse {
  readonly config: RoutingConfigRecord;
  readonly cache?: RoutingCacheConvergence;
  readonly mutation_receipt: GatewayMutationReceipt;
}

/**
 * `POST /internal/tenant-routing/configs/:configId/clone` — deep-copy any
 * config's targets + policies into a new customer config (epic #886).
 * Archived sources may be cloned; cloning is a read of the source.
 */
export interface CloneRoutingConfigRequest {
  readonly name: string;
  readonly description?: string | null;
  readonly assign_org_id?: string;
}

export interface CloneRoutingConfigResponse {
  readonly config: RoutingConfigRecord;
  readonly assignment: RoutingConfigAssignmentRecord | null;
  readonly cache?: RoutingCacheConvergence;
  readonly mutation_receipt: GatewayMutationReceipt;
  /** Copied-but-stranded pins (#1384 D09); see the from-default response. */
  readonly incompatible_pins?: readonly PinReconciliationRow[];
}

/** Required SKU-keyed catalog-routeability request. */
export interface SkuCatalogVisibilitySku {
  readonly sku_id: string;
  readonly namespace_slug: string;
  readonly vendor: string;
  readonly model: string;
  readonly protocol: string;
}

export interface ResolveSkuCatalogVisibilityRequest {
  readonly org_id: string;
  readonly skus: readonly SkuCatalogVisibilitySku[];
}

/**
 * One `(sku, protocol)` verdict — the grain the resolver actually computes
 * before it collapses to a SKU-id union for its HTTP callers.
 */
export interface SkuCatalogVisibilityPair {
  readonly sku_id: string;
  readonly protocol: string;
}

export interface ResolveSkuCatalogVisibilityResponse {
  /**
   * The any-protocol answer (#1355 Option A): a SKU is listable when it is
   * routable on AT LEAST ONE submitted protocol. This is the whole wire
   * contract — every HTTP caller reads only this field.
   */
  readonly visible_sku_ids: readonly string[];
  /**
   * Per-pair verdicts, for IN-PROCESS callers that need the protocol
   * dimension the union above destroys (#1761: the gateway serviceability
   * projection stores per-protocol rows).
   *
   * OPTIONAL ONLY BECAUSE IT ONCE CROSSED A WIRE. The
   * `/internal/tenant-routing/catalog-visibility` route it was optional for
   * was retired by #1405 PR-3, and both surviving callers (`/v1/models` and
   * the serviceability recompute) run in process, where the resolver always
   * populates it. A caller that needs pairs must therefore treat `undefined`
   * as a contract violation and fail loudly rather than degrade to the union —
   * silently reading `visible_sku_ids` back as a per-protocol verdict would
   * publish a cross-protocol union, which over-sells.
   */
  readonly visible_pairs?: readonly SkuCatalogVisibilityPair[];
}

/**
 * Derived outbound path for the winning SKU target (#1967 R5).
 * Same-region is always direct. Cross-region names the locally selected
 * path (or `direct` when this node has no scores yet). Observability
 * only — selection stays locally authoritative.
 */
export interface EffectiveEgressPath {
  readonly kind: 'direct' | 'via_exit';
  readonly path_id: string;
  readonly gateway_region: string;
  readonly dest_region: string | null;
  readonly link_id: string | null;
  readonly reason: 'same_region' | 'selected' | 'no_scores' | 'unknown_upstream' | 'pinned';
}

export interface GetEffectiveTenantRouteResponse {
  readonly route: EffectiveTenantRouteRecord;
  readonly egress_path?: EffectiveEgressPath;
  /** This endpoint reads DB truth directly; generation is only readiness evidence. */
  readonly cache_readiness?: {
    readonly authoritative: true;
    /** True only when the backend is readable and every requested scope is at the expected revision. */
    readonly ready: boolean;
    readonly cache_backend: 'ready' | 'unavailable';
    readonly generation: string | null;
    readonly expected_revision: string | null;
    readonly expected_scopes: readonly string[];
    readonly observed_revisions: Readonly<Record<string, string>>;
  };
}

/**
 * Epic #898 P5 (#907): one row of the SKU-partitioned target pool.
 * `upstream_channel_slug` is join-derived from `upstream_channels` at read
 * time — not stored on the pool row. #908 adds the endpoints serving these.
 */
export interface RoutingSkuTargetRecord {
  readonly id: string;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly vendor: string;
  readonly model: string;
  /**
   * Explicit physical supply selected for this canonical SKU. NULL is exposed
   * only for compatibility rows that could not be uniquely backfilled.
   */
  readonly capability_id: string | null;
  /** Read-only evidence joined from the pinned physical capability. */
  readonly capability_protocol: UpstreamProtocol | null;
  readonly upstream_model_name: string | null;
  readonly capability_status: string | null;
  readonly capability_last_passed_at: string | null;
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
  readonly upstream_channel_slug: string;
  readonly enabled: boolean;
  readonly weight: number;
  readonly retry_order: number | null;
  readonly priority: number;
  /** Declared target RPM capacity. NULL/omitted = no cap (L2 treats as unlimited). */
  readonly rpm_cap?: number | null;
  /** Declared target input-TPM capacity. NULL/omitted = no cap. */
  readonly tpm_cap?: number | null;
  /** Smart-routing role. NULL = external / no prior. */
  readonly smart_role?: 'self_pool' | 'external' | 'reserve' | null;
  /** Cold-start prior in [0, 1]. NULL = no prior. */
  readonly smart_initial_score?: number | null;
  /**
   * Operator note recorded when the target was manually disabled (0082),
   * e.g. "no balance, waiting for top-up". NULL while enabled or when the
   * operator left it blank. Omitted by gateways that predate 0082.
   */
  readonly disabled_note?: string | null;
  /** ISO time of the last manual enabled 1 -> 0 flip. NULL while enabled. */
  readonly disabled_at?: string | null;
  /** Operator (email, else ops user id) who made that flip. NULL while enabled. */
  readonly disabled_by?: string | null;
}

/** Read-only display metadata; never an input to routing or target writes. */
export interface RoutingTargetEgressDisplay {
  readonly binding_found: boolean;
  readonly egress_profile_id: string | null;
  readonly egress_profile_name: string | null;
  /** Null when the profile is missing or cannot be resolved. */
  readonly egress_profile_enabled: boolean | null;
  readonly has_override: boolean;
}

export interface RoutingTargetDisplayNamesResponse {
  readonly upstream_names_by_id: Readonly<Record<string, string | null>>;
  readonly account_labels_by_id: Readonly<Record<string, string | null>>;
  readonly key_bucket_labels_by_id: Readonly<Record<string, string | null>>;
  /**
   * Per target because a binding without an explicit profile uses the
   * capability's protocol-specific direct default (including probe_protocol).
   */
  readonly egress_by_target_id: Readonly<Record<string, RoutingTargetEgressDisplay>>;
}

/** Longest operator note accepted on a manual target disable (code points). */
export const TARGET_DISABLED_NOTE_MAX_CHARS = 200;

const TARGET_DISABLED_NOTE_CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/;

/**
 * Validate and normalise a manual-disable note. Trimmed; blank means no
 * note (null); control characters (including newlines) and more than
 * {@link TARGET_DISABLED_NOTE_MAX_CHARS} code points are rejected.
 */
export function normalizeTargetDisabledNote(
  raw: unknown,
): { readonly ok: true; readonly value: string | null } | { readonly ok: false; readonly reason: 'type' | 'control' | 'too_long' } {
  if (raw === null) return { ok: true, value: null };
  if (typeof raw !== 'string') return { ok: false, reason: 'type' };
  if (TARGET_DISABLED_NOTE_CONTROL_CHARS.test(raw)) return { ok: false, reason: 'control' };
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, value: null };
  if (Array.from(trimmed).length > TARGET_DISABLED_NOTE_MAX_CHARS) return { ok: false, reason: 'too_long' };
  return { ok: true, value: trimmed };
}

/** Epic #898 P5 (#907): per-SKU execution policy record. */
export interface RoutingSkuPolicyRecord {
  readonly sku_id: string;
  readonly policy_mode: 'full_chain' | 'network_only' | 'same_cache_domain' | 'fail_fast';
}

// ─── SKU-keyed routing CRUD + effective preview (epic #898 P5, #908) ────────

export interface ListRoutingSkuTargetsResponse {
  readonly targets: readonly RoutingSkuTargetRecord[];
  /**
   * Live candidate protocols for each exact target identity. Consumers use
   * this to evaluate protocol-partition impact without treating a target from
   * another protocol as surviving coverage.
   */
  readonly live_protocols_by_target_id: Readonly<Record<string, readonly UpstreamProtocol[]>>;
  /** Present on the global default-pool control-plane read. */
  readonly exclusive_assignment_impact?: RoutingDefaultSkuExclusiveImpact;
}

export interface RoutingDefaultSkuExclusiveImpact {
  readonly affected_active_configs: readonly RoutingConfigSkuCoverageImpact[];
  readonly affected_assigned_org_count: number;
  readonly default_covers_exclusive_configs: false;
  readonly message: 'default pool does not cover missing SKUs in exclusive assigned configs';
}

/**
 * Role-projected SKU-summary target row (#1658 part A). The ops-api
 * `/v1/ops/tenant-routing/configs/:id/sku-summary` proxy answers the full
 * `RoutingSkuTargetRecord` shape to admin sessions only; for every other
 * session it strips the supply identity (vendor, upstream / account /
 * key-bucket / upstream-channel ids, capability evidence) down to this
 * allowlist — a customer manager sees which SKUs are routed and how traffic
 * is dispatched, never WHERE it goes. The optional members model the
 * admin-only remainder so one wire type covers both projections.
 */
export interface RoutingConfigSkuSummaryTarget {
  readonly id: string;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly model: string;
  readonly enabled: boolean;
  readonly weight: number;
  readonly retry_order: number | null;
  readonly priority: number;
  readonly rpm_cap?: number | null;
  readonly tpm_cap?: number | null;
  /** Admin sessions only — absent from the sales projection. */
  readonly vendor?: string;
  readonly capability_id?: string | null;
  readonly capability_protocol?: UpstreamProtocol | null;
  readonly upstream_model_name?: string | null;
  readonly capability_status?: string | null;
  readonly capability_last_passed_at?: string | null;
  readonly upstream_id?: string;
  readonly account_id?: string;
  readonly key_bucket_id?: string;
  readonly upstream_channel_id?: string;
  readonly upstream_channel_slug?: string;
}

/**
 * Constant-query read model for a routing config's complete SKU partition.
 * The gateway returns every target and explicit policy in one bounded read;
 * callers treat a missing policy as the existing `full_chain` default.
 * The #1658 role projection is gone: #1724 narrowed `/v1/ops/tenant-routing` to
 * ops-admin outright, so ops-api proxies these records unprojected and every
 * other role gets a 403 instead of a reduced view.
 */
export interface GetRoutingConfigSkuSummaryResponse {
  readonly config: RoutingConfigSummaryRecord;
  readonly targets: readonly RoutingConfigSkuSummaryTarget[];
  readonly policies: readonly RoutingSkuPolicyRecord[];
}

/**
 * Upsert one SKU pool row. The writer supplies the SKU display identity
 * (`sku_name` / `vendor` / `model`) — the gateway stores snapshots and cannot
 * derive them from the platform-owned catalog (#909's ops proxy resolves them
 * before calling).
 */
export interface PutRoutingSkuTargetRequest {
  readonly sku_name: string;
  readonly vendor: string;
  readonly model: string;
  /** Physical capability pin; new writes must never create legacy unpinned rows. */
  readonly capability_id: string;
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
  /**
   * Optional optimistic-lock token for operator workflows. When present the
   * gateway performs the target write only if this durable upstream routing
   * revision still matches; stale callers receive ROUTING_SNAPSHOT_STALE.
   */
  readonly expected_routing_revision?: string;
  /** Omitted on update preserves the stored value; a new row defaults to 0. */
  readonly weight?: number;
  /** Omitted preserves the stored value; explicit null clears the retry order. */
  readonly retry_order?: number | null;
  /** Omitted on update preserves the stored value; a new row defaults to 0. */
  readonly priority?: number;
  /** Omitted on update preserves the stored value; a new row defaults to true. */
  readonly enabled?: boolean;
  /** Omitted on update preserves the stored value; a new row defaults to null (no cap). */
  readonly rpm_cap?: number | null;
  /** Omitted on update preserves the stored value; a new row defaults to null (no cap). */
  readonly tpm_cap?: number | null;
  /** Omitted preserves stored value; a new row defaults to null (external). */
  readonly smart_role?: 'self_pool' | 'external' | 'reserve' | null;
  /** Omitted preserves stored value; a new row defaults to null (no prior). */
  readonly smart_initial_score?: number | null;
  /**
   * Operator note for a manual disable (see {@link normalizeTargetDisabledNote}).
   * Only stored while the target ends up disabled: on the write that flips
   * `enabled` to false it is recorded with `disabled_at` / `disabled_by`; on an
   * already-disabled target it edits the note alone; flipping `enabled` back to
   * true clears all three. Omitted on an already-disabled target keeps the note.
   */
  readonly disabled_note?: string | null;
}

export interface PutRoutingSkuTargetResponse {
  readonly target: RoutingSkuTargetRecord;
  readonly cache?: RoutingCacheConvergence;
  /** Revision produced by this successful write, as a decimal string. */
  readonly routing_revision: string;
  readonly mutation_receipt?: GatewayMutationReceipt;
  /**
   * Present only on the global default-pool write
   * (`PUT /default-sku-targets/:skuId`), where a 200 does not imply the row
   * takes part in selection. See `RoutingDefaultPoolEffect`.
   */
  readonly default_pool_effect?: RoutingDefaultPoolEffect;
}

/**
 * Whether a row just written to `routing_default_sku_targets` participates in
 * candidate selection, and if not, what is shadowing it (#1902 C).
 *
 * The default pool sits at the BOTTOM of the ladder in
 * `generation/routing.ts`: the global branch returns whenever the globally
 * assigned config either covers the SKU or is `coverage_mode = 'exclusive'`,
 * so under an exclusive global assignment — which is what dev, staging and
 * production all run — nothing below it is ever consulted. The write still
 * succeeds and still answers 200, because the row is legitimate state that
 * becomes live the moment that config stops shadowing it. That is exactly why
 * the caller has to be told: it is not a no-op, it is DORMANT, and lifting the
 * shadow activates every row parked here at once.
 *
 * The shadow has TWO causes, and they take different remedies. An `exclusive`
 * global config shadows every SKU, and the fix is an `exclusive -> inherit`
 * edit. An `inherit` global config that happens to carry a target or policy row
 * for THIS SKU shadows it just as completely, and there the coverage mode is
 * already `inherit` — the fix is removing that covering row. Field names here
 * say "shadowing", never "coverage mode", so a caller branching on a name
 * cannot be steered at the wrong remedy; `message` names the actual cause and
 * `alternative_write_endpoint` is the same write in both cases.
 *
 * Deliberately not a rejection. A refusal would break the maintenance of a
 * default pool an operator is preparing for a future `inherit` config, and it
 * would turn a documented, currently-succeeding write into a 4xx for callers
 * that depend on it.
 */
export interface RoutingDefaultPoolEffect {
  /**
   * `false` when the active global assignment names a config that stops the
   * ladder for this SKU — either because it is `coverage_mode = 'exclusive'`,
   * or because it covers this SKU. Either way the ladder returns there and
   * never reaches the default pool. Org-scoped exclusive configs shadow the
   * pool only for their own orgs and do not clear this flag;
   * `GET /default-sku-targets/:skuId` enumerates those separately as
   * `exclusive_assignment_impact`.
   */
  readonly participates_in_global_routing: boolean;
  /** The globally assigned config doing the shadowing, when any. */
  readonly blocking_global_config: {
    readonly config_id: string;
    readonly config_name: string;
  } | null;
  /**
   * True exactly when a blocking config exists — the row is parked, not lost.
   * What has to change to un-park it depends on WHY it is shadowed, which
   * `message` states; this flag deliberately does not name a remedy.
   */
  readonly dormant_until_shadowing_removed: boolean;
  /** The write that DOES reach selection today, or `null` when none is needed. */
  readonly alternative_write_endpoint: string | null;
  readonly message: string;
}

export interface DeleteRoutingSkuTargetResponse {
  readonly ok: true;
  readonly id: string;
  readonly cache?: RoutingCacheConvergence;
  readonly mutation_receipt?: GatewayMutationReceipt;
}

export interface GetRoutingSkuPolicyResponse {
  readonly policy: RoutingSkuPolicyRecord | null;
}

export interface PutRoutingSkuPolicyRequest {
  readonly sku_name: string;
  readonly vendor: string;
  readonly model: string;
  readonly policy_mode: RoutingSkuPolicyRecord['policy_mode'];
}

export interface PutRoutingSkuPolicyResponse {
  readonly policy: RoutingSkuPolicyRecord;
  readonly cache?: RoutingCacheConvergence;
  readonly mutation_receipt?: GatewayMutationReceipt;
}

export interface DeleteRoutingSkuPolicyResponse {
  readonly ok: true;
  readonly sku_id: string;
  readonly cache?: RoutingCacheConvergence;
  readonly mutation_receipt?: GatewayMutationReceipt;
}

export interface RoutingConfigSkuCoverageTargetIdentity {
  readonly capability_id: string;
  /** Physical evidence populated by preview/apply responses. */
  readonly capability_protocol?: UpstreamProtocol;
  readonly upstream_model_name?: string;
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
}

export interface RoutingConfigSkuCoverageFillItem {
  readonly config_id: string;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly vendor: string;
  readonly model: string;
  readonly target: RoutingConfigSkuCoverageTargetIdentity;
}

export interface PreviewRoutingConfigSkuCoverageFillRequest {
  readonly items: readonly RoutingConfigSkuCoverageFillItem[];
}

export type RoutingConfigSkuCoverageFillDisposition = 'add' | 'conflict' | 'skipped';

export interface RoutingConfigSkuCoverageFillResult extends RoutingConfigSkuCoverageFillItem {
  readonly disposition: RoutingConfigSkuCoverageFillDisposition;
  readonly reason: 'missing' | 'already_exists' | 'explicit_coverage_exists' | 'capability_not_live' | 'sku_cost_missing';
}

export interface RoutingConfigSkuCoverageImpact {
  readonly config_id: string;
  readonly config_name: string;
  readonly coverage_mode: RoutingConfigCoverageMode;
  readonly affected_assigned_org_count: number;
  /**
   * Assignment scope that makes the config affected: `global` when the config
   * carries a global default assignment (possibly alongside org rows), else
   * `org`. Optional so preview/apply coverage-fill producers may omit it.
   */
  readonly scope?: 'global' | 'org';
}

export interface PreviewRoutingConfigSkuCoverageFillResponse {
  readonly results: readonly RoutingConfigSkuCoverageFillResult[];
  readonly impacts: readonly RoutingConfigSkuCoverageImpact[];
  readonly counts: { readonly add: number; readonly conflict: number; readonly skipped: number };
}

export interface ApplyRoutingConfigSkuCoverageFillRequest extends PreviewRoutingConfigSkuCoverageFillRequest {}

export interface ApplyRoutingConfigSkuCoverageFillResponse extends PreviewRoutingConfigSkuCoverageFillResponse {
  readonly inserted: number;
  readonly cache?: RoutingCacheConvergence;
  readonly mutation_receipt?: GatewayMutationReceipt;
}

/**
 * `POST /internal/tenant-routing/refresh-sku-names` (#996): bulk-refresh the
 * stored `sku_name` display snapshots on the six SKU routing tables after a
 * namespace slug rename. The gateway re-reads the full SKU catalog from
 * platform-api and rewrites rows by `sku_id` (idempotent; safe to re-run).
 */
export interface RefreshSkuNamesResponse {
  readonly ok: true;
  /** Rows rewritten across all six tables (0 when already current). */
  readonly refreshed_rows: number;
  /** SKU catalog entries fetched from platform-api. */
  readonly catalog_size: number;
}

// ─── global default ↔ platform default parity (#1918) ──────────────────────

/**
 * Which side of the comparison a SKU is missing from, or that both sides carry
 * it with different content.
 *
 * Severity is NOT a property of the kind alone — it is the kind crossed with
 * the global default config's `coverage_mode`, because that is what decides
 * which tier of the ladder serves the SKU:
 *
 *  - `missing_in_config` + `exclusive` — the live one. Tier 3 terminates
 *    unconditionally under `exclusive`, so a SKU the config does not carry
 *    resolves to zero candidates: it 404s and drops out of `/v1/models` for
 *    every org on the global default. This is the failure mode a newly
 *    launched SKU walks into.
 *  - `missing_in_config` + `inherit` — benign by construction. Falling through
 *    to the platform default table is what `inherit` is for; reporting it as
 *    drift would make the check permanently red on a correctly configured
 *    environment.
 *  - `mismatched` / `missing_in_default` — latent under both modes. The config
 *    covers the SKU, so tier 3 answers and the default table's version of that
 *    SKU is unreachable. It bites only after the global default is swapped or
 *    emptied, which is precisely when nobody is re-reading these two tables.
 *
 * `live_diff_count` on the response carries this classification so a reader
 * does not have to redo it.
 */
export type RoutingDefaultParityDiffKind =
  | 'missing_in_config'
  | 'missing_in_default'
  | 'mismatched';

/**
 * One target row reduced to the fields the comparison is defined over. `id` is
 * deliberately absent: the two tables mint their own row ids, so comparing
 * them would report every row as different.
 */
export interface RoutingDefaultParityTargetRow {
  readonly upstream_channel_id: string;
  readonly upstream_channel_slug: string;
  readonly enabled: boolean;
  readonly weight: number;
  readonly retry_order: number | null;
  readonly priority: number;
}

/** Per-SKU target divergence; both row lists are supplied so a reader can see each side. */
export interface RoutingDefaultParityTargetDiff {
  readonly kind: RoutingDefaultParityDiffKind;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly config_rows: readonly RoutingDefaultParityTargetRow[];
  readonly default_rows: readonly RoutingDefaultParityTargetRow[];
}

/** Per-SKU policy divergence; the absent side is null. */
export interface RoutingDefaultParityPolicyDiff {
  readonly kind: RoutingDefaultParityDiffKind;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly config_policy_mode: RoutingSkuPolicyRecord['policy_mode'] | null;
  readonly default_policy_mode: RoutingSkuPolicyRecord['policy_mode'] | null;
}

/**
 * `GET /internal/tenant-routing/global-default-parity` (#1918).
 *
 * Compares the CURRENT global default config's SKU partition against the
 * platform default tables (`routing_default_sku_targets` /
 * `routing_default_sku_policies`). The two are separate tables with separate
 * write surfaces and no synchronisation, so they drift silently.
 *
 * `global_config_id` is null when no global-scope assignment row exists. That
 * is a cold state, not drift: `in_sync` stays true and both diff lists are
 * empty, so a fresh environment does not raise a false alarm. A missing global
 * assignment is a different (and much louder) failure than parity drift, and
 * conflating them would train operators to ignore this signal.
 *
 * "Current global default" is read through the same active-assignment
 * definition the request path uses (`status = 'active'` plus the assignment
 * window), not through the bare assignment row. A check whose notion of the
 * subject differs from the ladder's would be a third definition of the thing
 * this feature exists to keep from drifting.
 */
export interface GlobalDefaultParityResponse {
  readonly global_config_id: string | null;
  /**
   * Coverage mode of that config, or null in the cold state. Diff severity is
   * undecidable without it — see `RoutingDefaultParityDiffKind`.
   */
  readonly coverage_mode: RoutingConfigCoverageMode | null;
  readonly checked_at: string;
  readonly in_sync: boolean;
  readonly target_diffs: readonly RoutingDefaultParityTargetDiff[];
  readonly policy_diffs: readonly RoutingDefaultParityPolicyDiff[];
  readonly counts: {
    readonly config_skus: number;
    readonly default_skus: number;
    readonly target_diffs: number;
    readonly policy_diffs: number;
    /**
     * Diffs that change what a tenant gets TODAY, under the current coverage
     * mode. A non-zero value is an outage-shaped finding; zero with a non-empty
     * diff list is bookkeeping debt. Presentation keys its urgency off this
     * rather than off the raw total, so a routinely-inherit environment does
     * not train operators to mute the alert.
     */
    readonly live_diffs: number;
  };
}

/**
 * `POST /internal/tenant-routing/global-default-parity/check` (#1918) — the
 * cron arm. Same read-only comparison plus at-most-one alert when it diverges.
 *
 * `alert` reports what happened to the notification, never to the data:
 * `not_needed` when in sync, `not_configured` when no webhook is bound (a
 * legitimate state in dev), `failed` when delivery was attempted and refused.
 * A failed delivery does NOT fail the check — the verdict is still valid.
 */
export interface CheckGlobalDefaultParityResponse extends GlobalDefaultParityResponse {
  readonly alert: 'not_needed' | 'sent' | 'not_configured' | 'failed';
}

// ─── routing config soft deprecation (#1919) ───────────────────────────────

/**
 * `PUT /internal/tenant-routing/configs/:configId/deprecation`.
 *
 * `deprecated: true` stamps the marker, `false` clears it. Both directions are
 * unconditional — marking a config that orgs are still bound to is a legal and
 * expected operation, because the marker only stops NEW bindings. This is the
 * whole point of the feature: the configs an operator most wants to flag are
 * exactly the ones they cannot be sure are unused.
 *
 * Deliberately NOT modelled on `status = 'archived'`, which the request path
 * filters on (`rc.status = 'active'`) and which therefore cuts a bound org's
 * routing over to the global default the moment it is set.
 */
export interface SetRoutingConfigDeprecationRequest {
  readonly deprecated: boolean;
}

export interface SetRoutingConfigDeprecationResponse {
  readonly config: RoutingConfigRecord;
  readonly mutation_receipt?: GatewayMutationReceipt;
}

/**
 * `POST /internal/tenant-routing/refresh-sku-catalog-snapshot` (#1902 B).
 *
 * Rebuilds `gateway_marketplace_sku_catalog_snapshot` from the published
 * `pub_sku_catalog`. `changed` is false when the published catalog already
 * matched, in which case `revision` is unchanged — the generation identifies
 * the CONTENT, so a no-op refresh must not look like a new snapshot.
 *
 * `removed_sku_ids` is listed, not counted: those identities disappear from the
 * two selector branches that join this table (`org_sku_route_overrides` and
 * `routing_legacy_fallback_sku_targets`), so a first rebuild against a stale
 * environment must be inspectable before and after.
 */
export interface RefreshSkuCatalogSnapshotResponse {
  readonly ok: true;
  readonly revision: string;
  readonly row_count: number;
  readonly catalog_hash: string;
  readonly added: number;
  readonly updated: number;
  readonly removed: number;
  readonly removed_sku_ids: readonly string[];
  /**
   * Rows whose join identity (`sku_name` / `vendor` / `model`) was rewritten.
   * Listed for the same reason as `removed_sku_ids`: four queries join the
   * snapshot to the frozen `routing_legacy_fallback_sku_targets` copy on all
   * three columns, so a rewrite drops that row exactly as a delete does — from
   * selection (`policy/selector.ts:959`, `:1057`) AND from both operator lists
   * that would explain the absence (`internal-tenant-routing-catalog.ts:313`,
   * `internal-tenant-routing-sku-targets.ts:2289`).
   */
  readonly updated_sku_ids: readonly string[];
  readonly changed: boolean;
}
